import { ChevronRightIcon, ChevronUpIcon } from "@primer/octicons-react";
import { Box, IconButton } from "@primer/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { saleSetApi } from "../../../api/saleSetApi";
import { showNotify } from "../../../helpers/toast";
import DataTable from "../../ui/data-table";
import { IColumn } from "../../ui/data-table/DataTable";
import { sf_saleset_viewmodel } from "../../../models/response/sale-set/sf_saleset";

type HocSinhCongNoProps = {
    ts_hocsinh_id: number;
    nam_hoc: string;
}

const HocSinhCongNo = (props: HocSinhCongNoProps) => {
  const [dataSource, setDataSource] = useState<sf_saleset_viewmodel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Map<string, string>>(new Map());

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(groupKey)) {
        newMap.delete(groupKey);
      } else {
        newMap.set(groupKey, groupKey);
      }
      return newMap;
    });
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ts_hocsinh_id, props.nam_hoc]);

  const loadData = async () => {
    if (!props.ts_hocsinh_id || !props.nam_hoc) return;

    setIsLoading(true);
    try {
      const res = await saleSetApi.selectByStudent({
        nam_hoc: props.nam_hoc,
        ts_hocsinh_id: props.ts_hocsinh_id,
      });

      if (res.is_success && res.data) {
        const data = res.data;
        if (Array.isArray(data) && data.length > 0) {
          setDataSource(data);
        } else {
          setDataSource([]);
        }
      } else {
        showNotify({ message: "Không thể tải dữ liệu công nợ", type: "warning" });
        setDataSource([]);
      }
    } catch (error) {
      console.error("Error loading cong no:", error);
      showNotify({ message: "Lỗi khi tải dữ liệu công nợ", type: "error" });
      setDataSource([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: any) => {
    if (value === null || value === undefined || value === 0) return "0";
    return new Intl.NumberFormat("vi-VN").format(value);
  };

  const tongHop = useMemo(() => {
    const total_dinh_muc = dataSource.reduce((sum, x) => sum + (x.so_tien_dinh_muc || 0), 0);
    const total_mien_giam = dataSource.reduce((sum, x) => sum + (x.so_tien_mien_giam || 0), 0);
    const total_phai_nop = dataSource.reduce((sum, x) => sum + (x.so_tien_phai_nop || 0), 0);
    const total_da_nop = dataSource.reduce((sum, x) => sum + (x.so_tien_da_nop || 0), 0);
    const total_thua_thieu = dataSource.reduce((sum, x) => sum + (x.so_tien_thua_thieu || 0), 0);

    return {
      total_dinh_muc,
      total_mien_giam,
      total_phai_nop,
      total_da_nop,
      total_thua_thieu,
    };
  }, [dataSource]);

  const flatDataWithGroups = useMemo(() => {
    const groups: { [key: string]: { group: string; items: sf_saleset_viewmodel[]; totals: any } } = {};

    const getOrder = (item: sf_saleset_viewmodel): { thuTuNhom: number; thuTu: number } => {
      return {
        thuTuNhom: Number(item.thutu_sapxep_nhomkhoannop) || 0,
        thuTu: Number(item.thutu_sapxep) || 0,
      };
    };

    dataSource.forEach((item) => {
      const groupKey = (item.sf_khoannop_group_id ?? "unknown").toString();
      if (!groups[groupKey]) {
        groups[groupKey] = {
          group: item.ten_khoan_nop_group || item.ma_khoan_nop_group || "Khác",
          items: [],
          totals: {
            total_dinh_muc: 0,
            total_mien_giam: 0,
            total_phai_nop: 0,
            total_da_nop: 0,
            total_thua_thieu: 0,
          },
        };
      }
      groups[groupKey].items.push(item);
      groups[groupKey].totals.total_dinh_muc += item.so_tien_dinh_muc || 0;
      groups[groupKey].totals.total_mien_giam += item.so_tien_mien_giam || 0;
      groups[groupKey].totals.total_phai_nop += item.so_tien_phai_nop || 0;
      groups[groupKey].totals.total_da_nop += item.so_tien_da_nop || 0;
      groups[groupKey].totals.total_thua_thieu += item.so_tien_thua_thieu || 0;
    });

    const flatData: any[] = [];
    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
      const ordersA = groups[a].items.map((x) => getOrder(x));
      const ordersB = groups[b].items.map((x) => getOrder(x));

      const minNhomA = Math.min(...ordersA.map((o) => o.thuTuNhom));
      const minNhomB = Math.min(...ordersB.map((o) => o.thuTuNhom));
      if (minNhomA !== minNhomB) return minNhomA - minNhomB;

      const minThuTuA = Math.min(...ordersA.map((o) => o.thuTu));
      const minThuTuB = Math.min(...ordersB.map((o) => o.thuTu));
      return minThuTuA - minThuTuB;
    });

    sortedGroupKeys.forEach((groupKey) => {
      const groupData = groups[groupKey];
      const isExpanded = expandedGroups.has(groupKey);

      const sortedItems = [...groupData.items].sort((x, y) => {
        const a = getOrder(x);
        const b = getOrder(y);
        if (a.thuTuNhom !== b.thuTuNhom) return a.thuTuNhom - b.thuTuNhom;
        if (a.thuTu !== b.thuTu) return a.thuTu - b.thuTu;
        return String(x.ma_khoan_nop || "").localeCompare(String(y.ma_khoan_nop || ""));
      });

      flatData.push({
        isGroupHeader: true,
        groupKey: groupKey,
        ten_khoan_nop_group: groupData.group,
        totals: groupData.totals,
        ma_khoan_nop: "",
        ten_khoan_nop: "",
        so_tien_dinh_muc: null,
        so_tien_mien_giam: null,
        so_tien_phai_nop: null,
        so_tien_da_nop: null,
        so_tien_thua_thieu: null,
        is_da_hach_toan: false,
        ghi_chu: "",
      });

      if (isExpanded) {
        sortedItems.forEach((item) => {
          flatData.push({
            ...item,
            isGroupHeader: false,
            parentGroup: groupKey,
          });
        });
      }
    });

    return flatData;
  }, [dataSource, expandedGroups]);

  useEffect(() => {
    if (dataSource.length > 0) {
      const newExpandedGroups = new Map<string, string>();
      const uniqueGroups = new Set<string>();

      dataSource.forEach((item) => {
        const groupKey = (item.sf_khoannop_group_id ?? "unknown").toString();
        uniqueGroups.add(groupKey);
      });

      uniqueGroups.forEach((groupKey) => {
        newExpandedGroups.set(groupKey, groupKey);
      });

      setExpandedGroups(newExpandedGroups);
    }
  }, [dataSource]);

  const columns = useMemo<IColumn[]>(() => {
    return [
      {
        caption: "Nhóm khoản nộp",
        dataField: "ten_khoan_nop_group",
        width: 300,
        fixed: true,
        fixedPosition: "left",
        cellRender: (data: any) => {
          const level = data.isGroupHeader ? 0 : 1;
          const isExpanded = expandedGroups.has(data.groupKey || data.parentGroup);

          if (data.isGroupHeader) {
            return (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  icon={!isExpanded ? ChevronRightIcon : ChevronUpIcon}
                  aria-label=""
                  variant="invisible"
                  size="small"
                  onClick={() => toggleGroup(data.groupKey)}
                />
                <Box
                  sx={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    marginLeft: "4px",
                    color: "#0969da",
                  }}
                >
                  {data.ten_khoan_nop_group}
                </Box>
              </Box>
            );
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
              <span>{data.ten_khoan_nop_group || ""}</span>
            </Box>
          );
        },
      },
      {
        caption: "Mã khoản nộp",
        dataField: "ma_khoan_nop",
        width: 150,
        fixed: true,
        fixedPosition: "left",
        cellRender: (data: any) => {
          if (data.isGroupHeader) {
            return <span></span>;
          }
          const level = 1;
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
              <span>{data.ma_khoan_nop}</span>
            </Box>
          );
        },
      },
      {
        caption: "Tên khoản nộp",
        dataField: "ten_khoan_nop",
        width: 250,
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          const level = 1;
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
              <span>{data.ten_khoan_nop}</span>
            </Box>
          );
        },
      },
      {
        caption: "Định mức",
        dataField: "so_tien_dinh_muc",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return <span>{formatCurrency(data.so_tien_dinh_muc)}</span>;
        },
      },
      {
        caption: "Miễn giảm",
        dataField: "so_tien_mien_giam",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return <span>{formatCurrency(data.so_tien_mien_giam)}</span>;
        },
      },
      {
        caption: "Phải nộp",
        dataField: "so_tien_phai_nop",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return <span>{formatCurrency(data.so_tien_phai_nop)}</span>;
        },
      },
      {
        caption: "Đã nộp",
        dataField: "so_tien_da_nop",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return <span>{formatCurrency(data.so_tien_da_nop)}</span>;
        },
      },
      {
        caption: "Thừa/Thiếu",
        dataField: "so_tien_thua_thieu",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          const value = data.so_tien_thua_thieu || 0;
          const color = value > 0 ? "#1a7f37" : value < 0 ? "#d1242f" : "#666";
          return (
            <span style={{ color, fontWeight: "bold" }}>
              {formatCurrency(value)}
            </span>
          );
        },
      },
      {
        caption: "Đã hạch toán",
        dataField: "is_da_hach_toan",
        width: 100,
        align: "center",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return data.is_da_hach_toan ? (
            <span style={{ color: "#1a7f37", fontSize: "16px", fontWeight: "bold" }}>✓</span>
          ) : (
            <span></span>
          );
        },
      },
      {
        caption: "Ghi chú",
        dataField: "ghi_chu",
        width: 200,
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return <span>{data.ghi_chu || ""}</span>;
        },
      },
    ];
  }, [expandedGroups, toggleGroup]);

  return (
    <Box sx={{ p: 3 }}>
      {isLoading ? (
        <Box sx={{ textAlign: "center", padding: 3 }}>Đang tải dữ liệu...</Box>
      ) : dataSource.length === 0 ? (
        <Box sx={{ textAlign: "center", padding: 3 }}>Không có dữ liệu công nợ</Box>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
              gap: 1,
              mb: 1,
              p: 1,
              border: "1px solid",
              borderColor: "border.default",
              borderRadius: 2,
              backgroundColor: "canvas.subtle",
            }}
          >
            <Box>
              <Box sx={{ fontSize: "11px", color: "fg.muted", mb: 0.5 }}>Định mức</Box>
              <Box sx={{ fontSize: "14px", fontWeight: 600 }}>{formatCurrency(tongHop.total_dinh_muc)}</Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: "11px", color: "fg.muted", mb: 0.5 }}>Miễn giảm</Box>
              <Box sx={{ fontSize: "14px", fontWeight: 600 }}>{formatCurrency(tongHop.total_mien_giam)}</Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: "11px", color: "fg.muted", mb: 0.5 }}>Phải nộp</Box>
              <Box sx={{ fontSize: "14px", fontWeight: 600 }}>{formatCurrency(tongHop.total_phai_nop)}</Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: "11px", color: "fg.muted", mb: 0.5 }}>Đã nộp</Box>
              <Box sx={{ fontSize: "14px", fontWeight: 600 }}>{formatCurrency(tongHop.total_da_nop)}</Box>
            </Box>
            <Box>
              <Box sx={{ fontSize: "11px", color: "fg.muted", mb: 0.5 }}>Thừa/Thiếu</Box>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color:
                    tongHop.total_thua_thieu > 0
                      ? "#1a7f37"
                      : tongHop.total_thua_thieu < 0
                        ? "#d1242f"
                        : "fg.default",
                }}
              >
                {formatCurrency(tongHop.total_thua_thieu)}
              </Box>
            </Box>
          </Box>

          <DataTable
            data={flatDataWithGroups}
            columns={columns}
            height={`${window.innerHeight - 400}px`}
            isLoading={isLoading}
            paging={{ enable: false }}
            searchEnable={false}
            exportEnable={false}
          />
        </>
      )}
    </Box>
  );
};

export default HocSinhCongNo;
